/**
 * MetaCollectionDetails — Ad details form for Meta Collection ad format.
 *
 * A Collection ad pairs a full-width cover image or video with a grid of three
 * product images below it. Tapping opens an Instant Experience — a full-screen,
 * fast-loading landing page built for mobile.
 *
 * - Separate cover assets for feed (4:5) and story (9:16) placements
 * - Primary text and headline are feed fields; the story cover has no text overlay
 * - Three product images with titles are shared across both placements
 * - Destination URL, CTA, and legal are shared
 */
import { useState, useEffect, useRef } from 'react';
import { Upload, X, Play } from 'lucide-react';
import { createMediaBlobUrl } from '@/utils/blobMediaType';
import { seedSignature } from './seedSignature';
import type { FieldIssue } from '../../types';
import type { Campaign, ValidationFeedback } from '@/api/campaigns';
import { FieldLabel, InlineIssue, Field, inputClass, issueBorder } from './shared';
import { GearExchangeModal, type GXImportedFile } from '../gear-exchange/GearExchangeModal';
import { GearExchangeImportButton } from '../gear-exchange/GearExchangeImportButton';

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIMARY_TEXT_LIMIT = 125;
const PRODUCT_TITLE_LIMIT = 25;

const CTA_OPTIONS = [
  'Apply Now',
  'Book Now',
  'Contact Us',
  'Get Offer',
  'Get Quote',
  'Learn More',
  'Order Now',
  'Shop Now',
  'Sign Up',
];

// ─── Cover asset upload zone ──────────────────────────────────────────────────

function CoverSlot({
  aspectLabel,
  aspectRatio,
  sizeClass,
  onChange,
  onClear,
  existingUrl,
  existingContentType,
  hasError,
  controlledFile,
}: {
  aspectLabel: string;
  aspectRatio: string;
  sizeClass: string;
  onChange: (f: File) => void;
  onClear?: () => void;
  existingUrl?: string;
  existingContentType?: string | null;
  hasError?: boolean;
  controlledFile?: File | null;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);

  // Accept file set from parent (e.g. bulk drag & drop)
  useEffect(() => {
    if (controlledFile) {
      setFile(controlledFile);
      onChange(controlledFile);
    }
  }, [controlledFile]); // eslint-disable-line react-hooks/exhaustive-deps
  const [blobSrc, setBlobSrc] = useState<string | undefined>(undefined);
  const [clearedExisting, setClearedExisting] = useState(false);

  useEffect(() => {
    if (!file) {
      setBlobSrc(undefined);
      return;
    }
    const url = URL.createObjectURL(file);
    setBlobSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // If the user cleared the existing image, don't fall back to existingUrl
  const displayUrl = clearedExisting ? blobSrc : (blobSrc ?? existingUrl);
  const isVideo = file
    ? file.type.startsWith('video/')
    : !!(
        existingContentType?.startsWith('video/') ||
        ['.mp4', '.mov', '.webm'].some((ext) =>
          (existingUrl ?? '').toLowerCase().split('?')[0].endsWith(ext),
        )
      );

  function handlePick(f: File) {
    setFile(f);
    onChange(f);
  }

  function handleClear(e: React.MouseEvent) {
    e.stopPropagation();
    setFile(null);
    setClearedExisting(true);
    onClear?.();
  }

  return (
    <div
      onClick={() => inputRef.current?.click()}
      className={`relative ${sizeClass} rounded-xl overflow-hidden bg-slate-100 border group transition-colors flex flex-shrink-0 cursor-pointer ${!displayUrl && hasError ? 'border-red-400 ring-1 ring-red-300' : 'border-slate-200 hover:border-slate-300'}`}
      style={{ aspectRatio }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handlePick(f);
          e.target.value = '';
        }}
      />
      {displayUrl ? (
        <>
          {isVideo ? (
            <video
              src={displayUrl}
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-cover pointer-events-none"
              onLoadedData={(e) => {
                e.currentTarget.currentTime = 0.5;
              }}
            />
          ) : (
            <img src={displayUrl} alt={aspectLabel} className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Upload className="w-4 h-4 text-white" />
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
          >
            <X className="w-3 h-3" />
          </button>
        </>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-1.5">
          <Upload className="w-4 h-4 text-slate-400" />
          <span className="text-[10px] text-slate-400 font-medium text-center leading-tight px-1">
            {aspectLabel}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Product thumbnail (image upload only) ────────────────────────────────────

function ProductThumb({
  index,
  file,
  existingUrl,
  existingContentType,
  onFileChange,
  onClear,
  hasError,
}: {
  index: number;
  file: File | null;
  existingUrl?: string;
  existingContentType?: string | null;
  onFileChange: (f: File | null) => void;
  onClear?: () => void;
  hasError?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [blobSrc, setBlobSrc] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!file) {
      setBlobSrc(undefined);
      return;
    }
    const url = URL.createObjectURL(file);
    setBlobSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const displayUrl = blobSrc ?? existingUrl;
  const isVideo = file
    ? file.type.startsWith('video/')
    : !!(
        existingContentType?.startsWith('video/') ||
        ['.mp4', '.mov', '.webm'].some((ext) =>
          (existingUrl ?? '').toLowerCase().split('?')[0].endsWith(ext),
        )
      );

  return (
    <div
      onClick={() => inputRef.current?.click()}
      className={`relative w-[46px] h-[46px] flex-shrink-0 rounded-lg overflow-hidden bg-slate-100 border group transition-colors cursor-pointer ${!displayUrl && hasError ? 'border-red-400 ring-1 ring-red-300' : 'border-slate-200 hover:border-slate-300'}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFileChange(f);
          e.target.value = '';
        }}
      />
      {displayUrl ? (
        <>
          {isVideo ? (
            <video
              src={displayUrl}
              muted
              playsInline
              preload="auto"
              onLoadedData={(e) => {
                e.currentTarget.currentTime = 0.1;
              }}
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={displayUrl}
              alt={`Product ${index + 1}`}
              className="w-full h-full object-cover"
            />
          )}
          {isVideo && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none group-hover:opacity-0 transition-opacity">
              <div className="w-5 h-5 rounded-full bg-black/50 flex items-center justify-center">
                <Play className="w-2.5 h-2.5 text-white fill-white ml-0.5" />
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Upload className="w-3 h-3 text-white" />
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onFileChange(null);
              onClear?.();
            }}
            className="absolute top-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
          >
            <X className="w-2 h-2" />
          </button>
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Upload className="w-3.5 h-3.5 text-slate-400" />
        </div>
      )}
      <div className="absolute top-1 left-1 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center bg-black/25 text-white">
        {index + 1}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface MetaCollectionInitialValues {
  caption?: string;
  url?: string;
  cta?: string;
  legal?: string;
  existingHeroUrl?: string;
  heroContentType?: string | null;
  products?: { title?: string; imageUrl?: string; existingAssetId?: string }[];
}

export interface MetaCollectionDetailsProps {
  campaignId?: string;
  fordshareIds?: string[] | null;
  campaign?: Campaign | null;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  initialValues?: MetaCollectionInitialValues;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onAIFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  // One cover image shared across all placements (CSS crops to fit each ratio)
  onHeroFileChange: (f: File) => void;
  onHeroClear?: () => void;
  onCaptionChange: (v: string) => void;
  onCtaChange: (v: string) => void;
  onProductsChange: (products: { imageUrl?: string; title?: string }[]) => void;
  onProductFilesChange: (files: (File | null)[], deletedIds: string[]) => void;
  onWebsiteChange: (v: string) => void;
  onUrlChange: (v: string) => void;
  onLegalChange: (v: string) => void;
  usedInVariants?: Map<string, number[]>;
  gxData?: import('@/api/gearExchange').GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
}

export function MetaCollectionDetails({
  campaignId,
  fordshareIds,
  campaign,
  showErrors,
  showWarnings,
  fieldIssues,
  initialValues,
  feedback,
  feedbackPending,
  onAIFeedback,
  onHeroFileChange,
  onHeroClear,
  onCaptionChange,
  onCtaChange,
  onProductsChange,
  onProductFilesChange,
  onWebsiteChange,
  onUrlChange,
  onLegalChange,
  usedInVariants,
  gxData,
  gxLoading,
  gxError,
}: MetaCollectionDetailsProps) {
  const [caption, setCaption] = useState(initialValues?.caption ?? '');
  const [destinationUrl, setDestinationUrl] = useState(initialValues?.url ?? '');
  const [cta, setCta] = useState(initialValues?.cta ?? 'Shop Now');
  const [legal, setLegal] = useState(initialValues?.legal ?? '');
  const [products, setProducts] = useState(() =>
    [0, 1, 2].map((i) => ({
      id: i + 1,
      file: null as File | null,
      title: initialValues?.products?.[i]?.title ?? '',
      existingUrl: initialValues?.products?.[i]?.imageUrl,
      existingAssetId: initialValues?.products?.[i]?.existingAssetId,
    })),
  );
  const [productDeletedIds, setProductDeletedIds] = useState<string[]>([]);
  // Signature of the products last seeded from — null means "never seeded".
  const seededSigRef = useRef<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [droppedHeroFile, setDroppedHeroFile] = useState<File | null>(null);

  // Gear Exchange state
  const [showGxModal, setShowGxModal] = useState(false);

  // Count filled slots: hero (if has image) + products with images
  const hasHero = !!(droppedHeroFile || initialValues?.existingHeroUrl);
  const filledProducts = products.filter((p) => p.file || p.existingUrl).length;
  const existingImageCount = (hasHero ? 1 : 0) + filledProducts;

  // Sync parent state with this form's initial product state on mount.
  // The parent's collectionProducts starts as [] and is only updated via notifyProducts,
  // so without this sync, buildSavePayload would use stale/empty product titles on save.
  useEffect(() => {
    onProductsChange(
      products.map((p) => ({
        imageUrl: p.existingUrl,
        title: p.title || undefined,
      })),
    );
    // Don't call onProductFilesChange here — no files have changed on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-seed from the DB whenever the incoming product identities change.
  // Same guard as the carousel/story/dynamic-creative components — see
  // seedSignature.ts. The useState initialiser above only runs on mount, which
  // left this component dependent on the parent's formKey remount to pick up new
  // data. An empty signature is never seeded from, because the parent publishes
  // an empty list as a loading placeholder while URL resolution is in flight.
  useEffect(() => {
    if (!initialValues?.products) return;
    const sig = seedSignature(initialValues.products);
    if (!sig || sig === seededSigRef.current) return;
    seededSigRef.current = sig;
    const next = [0, 1, 2].map((i) => ({
      id: i + 1,
      file: null as File | null,
      title: initialValues.products?.[i]?.title ?? '',
      existingUrl: initialValues.products?.[i]?.imageUrl,
      existingAssetId: initialValues.products?.[i]?.existingAssetId,
    }));
    setProducts(next);
    // A re-seed adopts server state wholesale, so deletions staged against the
    // previous seed are already reflected in initialValues.products.
    setProductDeletedIds([]);
    onProductsChange(
      next.map((p) => ({
        imageUrl: p.existingUrl,
        title: p.title || undefined,
      })),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.products]);

  // Notify parent of product mockup URLs whenever products change
  function notifyProducts(next: typeof products, deletedIds: string[]) {
    onProductsChange(
      next.map((p) => ({
        imageUrl: p.file ? createMediaBlobUrl(p.file) : p.existingUrl,
        title: p.title || undefined,
      })),
    );
    onProductFilesChange(
      next.map((p) => p.file),
      deletedIds,
    );
  }

  function updateProduct(
    id: number,
    patch: { file?: File | null; title?: string; existingUrl?: string; existingAssetId?: string },
  ) {
    const next = products.map((p) => (p.id === id ? { ...p, ...patch } : p));
    setProducts(next);
    notifyProducts(next, productDeletedIds);
  }

  function clearProduct(id: number) {
    const product = products.find((p) => p.id === id);
    const newDeletedIds = product?.existingAssetId
      ? [...productDeletedIds, product.existingAssetId]
      : productDeletedIds;
    if (product?.existingAssetId) setProductDeletedIds(newDeletedIds);
    const next = products.map((p) =>
      p.id === id ? { ...p, file: null, existingUrl: undefined, existingAssetId: undefined } : p,
    );
    setProducts(next);
    notifyProducts(next, newDeletedIds);
  }

  function handleDestinationUrlChange(v: string) {
    setDestinationUrl(v);
    onUrlChange(v);
    try {
      const hostname = new URL(v).hostname.replace(/^www\./, '');
      onWebsiteChange(hostname);
    } catch {
      onWebsiteChange(v);
    }
  }

  function handleBulkDrop(files: FileList | File[]) {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    let fileIdx = 0;
    // Fill cover if empty
    const hasHero = !!droppedHeroFile || !!initialValues?.existingHeroUrl;
    if (!hasHero && fileIdx < arr.length) {
      setDroppedHeroFile(arr[fileIdx++]);
    }
    // Fill empty product slots
    const nextProducts = products.map((p) => {
      if (fileIdx >= arr.length) return p;
      if (!p.file && !p.existingUrl) {
        return { ...p, file: arr[fileIdx++] };
      }
      return p;
    });
    setProducts(nextProducts);
    notifyProducts(nextProducts, productDeletedIds);
  }

  function onZoneDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDraggingFile(true);
  }
  function onZoneDragLeave(e: React.DragEvent) {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDraggingFile(false);
  }
  function onZoneDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDraggingFile(false);
    handleBulkDrop(e.dataTransfer.files);
  }

  // Determine if a field has errors or warnings
  function fieldError(key: string) {
    return showErrors && fieldIssues[key]?.errors?.length > 0;
  }
  function fieldWarn(key: string) {
    return showWarnings && fieldIssues[key]?.warnings?.length > 0;
  }
  function fieldBorder(key: string) {
    if (fieldError(key)) return 'border-red-400 ring-1 ring-red-300';
    if (fieldWarn(key)) return 'border-amber-400 ring-1 ring-amber-200';
    return '';
  }

  return (
    <div className="flex-1 overflow-y-auto hover-scroll px-6 py-5 space-y-6 min-h-0">
      {/* ── About ───────────────────────────────────────────────────── */}
      <div className="border-l-2 border-[#0014cc]/30 pl-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
          About this format
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          A Collection ad pairs a full-width cover image or video with a grid of three product
          images below it. Tapping the ad opens an Instant Experience — a full-screen, fast-loading
          landing page. Upload separate cover assets for{' '}
          <span className="font-medium text-slate-700">4:5 feed</span> and{' '}
          <span className="font-medium text-slate-700">9:16 story</span> placements. The product
          grid and copy are shared across both.{' '}
          <a
            href="https://www.facebook.com/business/ads/collection-ad-format/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-slate-600 underline underline-offset-2 decoration-slate-300"
          >
            Meta Collection specs ↗
          </a>
        </p>
      </div>

      {/* ── Gear Exchange Import ──────────────────────────────────── */}
      {
        <>
          <GearExchangeImportButton
            totalAssets={gxData?.total_assets ?? null}
            loading={gxLoading}
            error={gxError}
            fordshareIds={fordshareIds ?? undefined}
            onClick={() => setShowGxModal(true)}
          />

          {showGxModal && gxData && campaign && campaignId && (
            <GearExchangeModal
              data={gxData}
              campaign={campaign}
              campaignId={campaignId}
              formatLabel="Meta Collection"
              zoneLimits={[{ ratio: 'any', limit: 4, existing: existingImageCount }]}
              onClose={() => setShowGxModal(false)}
              usedInVariants={usedInVariants}
              onImport={(files: GXImportedFile[]) => {
                handleBulkDrop(files.map((f) => f.file));
              }}
            />
          )}
        </>
      }

      {/* ── Primary text ─────────────────────────────────────────────── */}
      <div>
        <FieldLabel
          label="Primary Text"
          tooltip="Shown above the cover image in feed. Up to 125 characters."
        />
        <div className="relative">
          <textarea
            value={caption}
            onChange={(e) => {
              setCaption(e.target.value);
              onCaptionChange(e.target.value);
            }}
            placeholder="Primary text..."
            rows={3}
            className={`${inputClass} resize-none pr-14 ${fieldBorder('primary_text')}`}
          />
          <InlineIssue
            issue={fieldIssues['primary_text']}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onAIFeedback}
          />
          <span
            className={`absolute right-3 bottom-2.5 text-[10px] tabular-nums pointer-events-none ${
              caption.length > PRIMARY_TEXT_LIMIT ? 'text-amber-600 font-medium' : 'text-slate-400'
            }`}
          >
            {caption.length}/{PRIMARY_TEXT_LIMIT}
          </span>
        </div>
      </div>

      {/* ── Images — cover + product thumbnails side by side ─────────── */}
      <div onDragOver={onZoneDragOver} onDragLeave={onZoneDragLeave} onDrop={onZoneDrop}>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
          Images
          {isDraggingFile && (
            <span className="normal-case font-normal tracking-normal ml-1 text-[#0014cc]">
              — drop to fill empty slots
            </span>
          )}
        </p>
        <div className="border-b border-slate-200 mb-3" />
        <div
          className={`flex gap-3 rounded-xl transition-all ${isDraggingFile ? 'ring-2 ring-[#0014cc]/30 bg-[#0014cc]/[0.02] p-2 -m-2' : ''}`}
        >
          {/* Cover */}
          <CoverSlot
            aspectLabel="Cover Image / Video"
            aspectRatio="4/5"
            sizeClass="w-[120px]"
            onChange={onHeroFileChange}
            onClear={() => {
              onHeroClear?.();
              setDroppedHeroFile(null);
            }}
            existingUrl={initialValues?.existingHeroUrl}
            existingContentType={initialValues?.heroContentType}
            hasError={fieldError('asset_files')}
            controlledFile={droppedHeroFile}
          />
          {/* Product rows — thumbnail + title */}
          <div className="flex-1 flex flex-col gap-1.5">
            {products.map((product, i) => (
              <div key={product.id} className="flex items-center gap-2">
                <ProductThumb
                  index={i}
                  file={product.file}
                  existingUrl={product.existingUrl}
                  onFileChange={(f) => updateProduct(product.id, { file: f })}
                  onClear={() => clearProduct(product.id)}
                  hasError={fieldError('asset_files')}
                />
                <div className="flex-1">
                  <div className="relative">
                    <input
                      type="text"
                      value={product.title}
                      onChange={(e) => updateProduct(product.id, { title: e.target.value })}
                      placeholder={`Product ${i + 1}...`}
                      className={`${inputClass} text-xs pr-10 ${fieldWarn('headline') ? 'border-amber-400 ring-1 ring-amber-200' : ''}`}
                    />
                    <span
                      className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] tabular-nums pointer-events-none ${
                        product.title.length > PRODUCT_TITLE_LIMIT
                          ? 'text-amber-600 font-medium'
                          : 'text-slate-400'
                      }`}
                    >
                      {product.title.length}/{PRODUCT_TITLE_LIMIT}
                    </span>
                  </div>
                  {showWarnings && fieldIssues['headline']?.warnings?.length > 0 && (
                    <div className="mt-0.5">
                      <InlineIssue
                        issue={fieldIssues['headline']}
                        showErrors={false}
                        showWarnings={showWarnings}
                        inline
                        feedback={feedback}
                        feedbackPending={feedbackPending}
                        onFeedback={onAIFeedback}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Image-block errors shown below the grid */}
        <InlineIssue
          issue={fieldIssues['asset_files']}
          showErrors={showErrors}
          showWarnings={false}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
      </div>

      {/* ── Destination URL + CTA ────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel
            label="Destination URL"
            tooltip="Opens the Instant Experience. Shared across both placements."
          />
          <input
            type="url"
            value={destinationUrl}
            onChange={(e) => handleDestinationUrlChange(e.target.value)}
            placeholder="https://www.ford.com/..."
            className={`${inputClass} ${fieldBorder('destination_url')}`}
          />
          <InlineIssue
            issue={fieldIssues['destination_url']}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onAIFeedback}
          />
        </div>
        <div>
          <FieldLabel label="Call to Action" tooltip="Shared across both placements." />
          <select
            value={cta}
            onChange={(e) => {
              setCta(e.target.value);
              onCtaChange(e.target.value);
            }}
            className={`${inputClass} cursor-pointer`}
          >
            {CTA_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Legal ───────────────────────────────────────────────────── */}
      <Field
        label="Legal"
        tooltip="Required in some markets (e.g. France) for regulated categories. Internal only — not displayed in the mockup."
        fieldKey="legal"
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        help={<>Enter "N/A" if your market does not require a legal disclaimer.</>}
      >
        <textarea
          value={legal}
          onChange={(e) => {
            setLegal(e.target.value);
            onLegalChange(e.target.value);
          }}
          placeholder="Legal disclaimer — required for some markets (e.g. FR)..."
          rows={5}
          className={`${inputClass} resize-none ${issueBorder(fieldIssues['legal'], showErrors, showWarnings)}`}
        />
      </Field>
    </div>
  );
}
